import { useState, useEffect, useRef, useContext } from 'react';
import { Avatar } from 'antd';
import { StarFilled } from '@ant-design/icons';
import { RiDoubleQuotesL, RiDoubleQuotesR } from 'react-icons/ri';
import { useDoctorQuery } from '../../hooks/useDoctorQuery';
import drEasyApi from '../../Api/Customapi';
import { getImg } from '../../Api/getImg';
import { useDoctorContext } from '../../context/DoctorContext';
import { useLanguage } from '../../context/LanguageContext';

export default function CustomTestimonialCarousel() {
    const [slidesToShow, setSlidesToShow] = useState(1);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [testimonials, setTestimonials] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const timeoutRef = useRef(null);
    const { data: doctor } = useDoctorQuery();
    const { setHasReviews } = useDoctorContext();
    const { t, language } = useLanguage();

    useEffect(() => {
        const updateSlides = () => {
            if (window.innerWidth < 768) setSlidesToShow(1);
            else if (window.innerWidth < 1024) setSlidesToShow(2);
            else if (window.innerWidth < 1600) setSlidesToShow(3);
            else setSlidesToShow(4);
        };

        updateSlides();
        window.addEventListener('resize', updateSlides);
        return () => window.removeEventListener('resize', updateSlides);
    }, []);

    useEffect(() => {
        const getTestimonials = async () => {
            if (!doctor?.id) return;

            try {
                setLoading(true);
                const res = await drEasyApi.get("/api/Review/GetReviews", {
                    params: { DoctorId: doctor.id }
                });

                if (res?.data && Array.isArray(res.data)) {
                    setTestimonials(res.data);
                    setHasReviews(true)
                    setError(null);
                } else {
                    setTestimonials([]);
                }
            } catch (error) {
                console.error("Error fetching testimonials:", error);
                setError("Failed to load testimonials");
                setTestimonials([]);
            } finally {
                setLoading(false);
            }
        };

        getTestimonials();
    }, [doctor?.id]);

    const maxIndex = testimonials.length;

    useEffect(() => {
        if (testimonials.length === 0) return;

        timeoutRef.current = setTimeout(() => {
            setCurrentIndex((prevIndex) => (prevIndex + 1) % maxIndex);
        }, 3000);

        return () => clearTimeout(timeoutRef.current);
    }, [currentIndex, maxIndex, testimonials.length]);

    const getDisplayName = (userName) => {
        if (!userName) return t('testimonials.anonymous');

        const emailPart = userName.split('__')[0];

        if (emailPart.includes('@')) {
            return emailPart.split('@')[0];
        }

        return emailPart || t('testimonials.anonymous');
    };

    const getAvatarProps = (userName, userId) => {
        const displayName = getDisplayName(userName);

        
        const avatarUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&background=random&color=fff&size=56`;

        return {
            src: avatarUrl,
            alt: `${displayName}'s avatar`
        };
    };

    // Get visible slides with wrap-around logic
    const displayItems = [];
    if (testimonials.length > 0) {
        for (let i = 0; i < Math.min(slidesToShow, testimonials.length); i++) {
            displayItems.push(testimonials[(currentIndex + i) % testimonials.length]);
        }
    }

    if (doctor?.isLocked) {
        return null;
    }

    if (loading) {
        return (
            <section className="p-4 w-full sm:px-6 lg:p-10">
                <div className="text-center mb-14">
                    <h2 className="text-4xl font-bold text-textPrimary">{t('testimonials.title')}</h2>
                    <p className="mt-3 text-textPrimary/70 max-w-2xl mx-auto">
                        {t('common.loading')}
                    </p>
                </div>
            </section>
        );
    }

    if (error || testimonials.length === 0) {
        return null
    }

    return (
        <section className="p-4 w-full sm:px-6 lg:p-10">
            <div className="text-center mb-14">
                <h2 className="text-4xl font-bold text-textPrimary">{t('testimonials.title')}</h2>
                <p className="mt-3 text-textPrimary/70 max-w-2xl mx-auto">
                    {t('testimonials.description')}
                </p>
            </div>

            <div className="overflow-hidden w-full">
                <div
                    className="flex transition-transform duration-700 ease-in-out justify-center"
                    style={{ width: '100%' }}
                >
                    {displayItems.map((testimonial, index) => {
                        const displayName = getDisplayName(testimonial.userName);
                        const avatarProps = getAvatarProps(testimonial.userName, testimonial.userId);

                        return (
                            <div
                                key={testimonial.id}
                                className="px-3 flex-shrink-0"
                                style={{ width: `${100 / slidesToShow}%` }}
                            >
                                <article
                                    className="bg-primary/10 border border-primary/20 rounded-xl p-6 shadow-md h-full flex flex-col justify-between hover:shadow-xl transition-shadow duration-300 focus:outline-none focus:ring-4 focus:ring-primary"
                                    tabIndex={0}
                                    aria-label={`${t('testimonials.testimonialFrom')} ${displayName}`}
                                >
                                    {/* Column layout: avatar, name, rating */}
                                    <div className="flex flex-col gap-1 mb-3">{testimonial?.userProfile ? (<img src={getImg (testimonial?.userProfile)} alt={`${displayName}'s profile`} className="rounded-full w-10 object-cover h-10 md:w-12 md:h-12" />
                                    ) : (  <Avatar
                                            {...avatarProps}
                                            size={56}
                                        />)}
                                      
                                        <p className="font-semibold text-gray-900 text-lg m-0">
                                            {displayName}
                                        </p>
                                        <div
                                            className="text-yellow-500 flex"
                                            aria-label={`${t('testimonials.rating')}: ${testimonial.rating} ${t('testimonials.stars')}`}
                                            role="img"
                                            aria-live="polite"
                                        >
                                            {Array(5)
                                                .fill(0)
                                                .map((_, i) => (
                                                    <StarFilled
                                                        key={i}
                                                        className={`text-lg ${i < testimonial.rating
                                                                ? 'text-yellow-500'
                                                                : 'text-gray-300'
                                                            }`}
                                                    />
                                                ))}
                                        </div>
                                    </div>

                                    {/* Review text with quote icons */}
                                    <p className="text-gray-700 text-sm leading-relaxed italic relative text-center px-3 mb-auto">
                                        <RiDoubleQuotesL className="inline-block text-primary text-2xl -translate-y-1 mr-1" />
                                        {testimonial.text}
                                        <RiDoubleQuotesR className="inline-block text-primary text-2xl -translate-y-1 ml-1" />
                                    </p>

                                    {/* Optional: Show review date */}
                                    <div className="text-xs text-gray-500 mt-3 text-center">
                                        {new Date(testimonial.createdAt).toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-US')}
                                    </div>
                                </article>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Only show dots if we have testimonials */}
            {testimonials.length > 1 && (
                <div className="flex justify-center mt-6 space-x-3">
                    {Array(maxIndex)
                        .fill(0)
                        .map((_, index) => (
                            <button
                                key={index}
                                onClick={() => setCurrentIndex(index)}
                                aria-label={`${t('testimonials.goToTestimonial')} ${index + 1}`}
                                className={`w-4 h-4 rounded-full transition-colors ${currentIndex === index
                                        ? 'bg-primary'
                                        : 'bg-gray-300 hover:bg-primary/70'
                                    }`}
                            />
                        ))}
                </div>
            )}
        </section>
    );
}